/** @jsxImportSource @emotion/react */

import { msg } from '@lingui/core/macro';
import { AlertCircle, FileQuestion, Plus } from 'lucide-react';

import { Board, Template } from 'modules/core/domain/interfaces/supabase/overlay/entity';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import Header from 'modules/template-editor/ui/components/catalog-card/header';
import { Trans, useLingui } from '@lingui/react/macro';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { GridLoader } from 'react-spinners';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { deriveAsyncState } from 'shared/utils/async-state';
import { AppDispatch } from 'store/store';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import UDAsyncContent from 'ud-ui/components/async-content';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import UDButton from 'ud-ui/components/button';
import UDEmptyState from 'ud-ui/components/empty-state';
import EmptyButton from 'ud-ui/components/empty-button';
import useFabric from 'modules/overlay-canvas/hooks/use-fabric';
import BoardItem from 'modules/template-editor/ui/components/view-boards/boards-list/board-item';
import BoardsList from 'modules/template-editor/ui/components/view-boards/boards-list/boards-list';
import { CanvasWrapper, Container, LeftContainer, RightContainer } from 'modules/template-editor/ui/components/view-boards/styles';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { addView, fetchBoardsByTemplateId } from 'modules/template-editor/store/templates/actions';
import {
  selectActiveTemplateId,
  selectBoardsByTemplateId,
  selectCatalogTemplatesById,
  selectFailedTemplates,
  selectLoadedTemplates,
} from 'modules/template-editor/store/templates/selectors';
import { setActiveBoard, setActiveTemplate } from 'modules/template-editor/store/templates/templates';
import { useCatalogTemplates } from 'modules/template-editor/ui/hooks/use-catalog-templates';
import useCreateModal from '../hooks/modals/board/create';
import useBoards from '../hooks/use-boards';
import { MainBlock } from './styles';
import { theme } from 'styles/theme/theme';

/** `:id` из адреса — целое положительное число, иначе шаблона по такой ссылке нет. */
function parseTemplateId(raw: string | undefined): number | undefined {
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : undefined;
}

/**
 * Карточка готового шаблона `/template/catalog/:id`. Сама грузит каталог (`useCatalogTemplates`) и табло шаблона
 * из адреса — открывается по прямой ссылке и после F5, а не только кликом из списка (IMGM-1574). Шаблон ищется
 * только в каталоге: по ссылке открывается ровно то, что пользователь видит в списке.
 */
function CatalogCard() {
  const { t } = useLingui();
  const dispatch = useDispatch<AppDispatch>();
  const dispatchWithToast = useDispatchWithToast();
  const navigate = useNavigate();
  const { setHeaderContent } = useMainLayout();
  const templateId = parseTemplateId(useParams().id);

  const { status: catalogStatus, retry } = useCatalogTemplates();
  const catalogById = useSelector(selectCatalogTemplatesById);
  const template = templateId === undefined ? undefined : (catalogById[templateId] as Template | undefined);
  const activeTemplateId = useSelector(selectActiveTemplateId);
  const loadedTemplates = useSelector(selectLoadedTemplates);
  const failedTemplates = useSelector(selectFailedTemplates);

  // По id, а не по объекту: объект шаблона меняется с каждым просмотром и лайком (`addView`, `addLike`).
  const foundTemplateId = template?.id;
  const isBoardsLoaded = foundTemplateId !== undefined && loadedTemplates.includes(foundTemplateId);
  const isBoardsSettled = isBoardsLoaded || (foundTemplateId !== undefined && failedTemplates.includes(foundTemplateId));

  useEffect(() => {
    if (foundTemplateId !== undefined) dispatch(setActiveTemplate({ id: foundTemplateId }));
  }, [dispatch, foundTemplateId]);

  // Упавший запрос не повторяется по кругу: `isBoardsLoaded` после сбоя не меняется, эффект не перезапускается.
  useEffect(() => {
    if (foundTemplateId === undefined || isBoardsLoaded) return;
    dispatchWithToast(fetchBoardsByTemplateId({ templateId: foundTemplateId }), { error: msg`Не удалось загрузить табло` });
  }, [dispatchWithToast, foundTemplateId, isBoardsLoaded]);

  // Канва создаётся с разрешением шаблона, поэтому содержимое монтируется, когда есть и шаблон, и его табло, а
  // активным стал шаблон из адреса, а не прошлый.
  const isReady = template !== undefined && isBoardsSettled && activeTemplateId === template.id;
  const state = deriveAsyncState({
    itemsCount: isReady ? 1 : 0,
    hasCurrentData: true,
    isFetching: false,
    isPending: templateId !== undefined && (catalogStatus === 'loading' || (template !== undefined && !isReady)),
    isError: catalogStatus === 'error',
  });

  const templateName = template?.name;
  const breadcrumb = useMemo(
    () => (
      <UDBreadcrumbs
        className="my-10"
        breadcrumbs={[
          { label: t`Трансляции`, to: Pages.TEMPLATE.INDEX },
          { label: t`Готовые шаблоны`, to: Pages.TEMPLATE.CATALOG.INDEX },
          ...(templateId !== undefined && templateName
            ? [{ label: templateName, to: Pages.TEMPLATE.CATALOG.builders.card(templateId) }]
            : []),
        ]}
      />
    ),
    [t, templateId, templateName],
  );

  useEffect(() => {
    setHeaderContent(breadcrumb);
    return () => setHeaderContent(null);
  }, [breadcrumb, setHeaderContent]);

  const toCatalog = (
    <UDButton variant="primary" type="button" onClick={() => navigate(Pages.TEMPLATE.CATALOG.INDEX)}>
      <Trans>К готовым шаблонам</Trans>
    </UDButton>
  );

  return (
    <UDAsyncContent
      className="h-full"
      state={state}
      loading={<GridLoader />}
      empty={
        <UDEmptyState
          className="max-w-xl"
          icon={<FileQuestion size={28} />}
          title={<Trans>Шаблон не найден</Trans>}
          subtitle={<Trans>Возможно, его удалили, скрыли из каталога или ссылка устарела.</Trans>}
          action={toCatalog}
        />
      }
      error={
        <UDEmptyState
          className="max-w-xl"
          icon={<AlertCircle size={28} />}
          title={<Trans>Не удалось загрузить шаблон</Trans>}
          subtitle={<Trans>Проверьте соединение и попробуйте ещё раз</Trans>}
          action={
            <UDButton variant="primary" type="button" onClick={retry}>
              <Trans>Повторить</Trans>
            </UDButton>
          }
        />
      }
    >
      {template && <CatalogCardContent template={template} />}
    </UDAsyncContent>
  );
}

interface CatalogCardContentProps {
  template: Template;
}

function CatalogCardContent({ template }: CatalogCardContentProps) {
  const { t } = useLingui();
  const dispatch = useDispatch<AppDispatch>();
  const dispatchWithToast = useDispatchWithToast();
  const { info } = useOrganizer();
  const selectBoards = useMemo(() => selectBoardsByTemplateId(template.id), [template.id]);
  const boards = useSelector(selectBoards);
  const { activeBoard } = useBoards();

  useEffect(() => {
    if (!activeBoard || !boards.find((board) => board.id === activeBoard.id)) {
      if (!boards || boards.length <= 0) {
        return;
      }
      dispatch(setActiveBoard({ id: boards[0].id }));
    }
  }, [activeBoard, boards, dispatch]);

  const { canvas, manager } = useFabric('static', {
    data: { ...template.resolution },
    style: { borderRadius: 8, position: 'absolute' },
  });

  useEffect(() => {
    if (!manager) {
      return;
    }
    const resizeCanvas = () => {
      manager.setParentSize();
      manager.normalizedViewport();
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [manager]);

  useEffect(() => {
    if (!manager) {
      return;
    }
    if (!activeBoard) {
      return;
    }

    manager.loadLocal({
      scheme: { objects: activeBoard.objects, version: activeBoard.version },
      safeBackground: true,
    });
  }, [activeBoard, manager]);

  const onClick = (board: Board) => {
    dispatch(setActiveBoard({ id: board.id }));
  };

  useEffect(() => {
    if (!info) {
      return;
    }
    dispatchWithToast(addView({ templateId: template.id, userId: info.id }), { error: msg`Не удалось засчитать просмотр` });
  }, [dispatchWithToast, template.id, info]);

  const { modal: createModal, open: openCreateModal } = useCreateModal();

  const isAdmin = useIsAdmin();

  return (
    <>
      <MainBlock>
        <Container>
          <LeftContainer>
            <BoardsList
              items={boards.map((board) => {
                return (
                  <BoardItem
                    key={board.id}
                    board={board}
                    isEditable={isAdmin}
                    onClick={() => onClick(board)}
                    isActive={board.id === activeBoard?.id}
                    isDefaultTemplate={true}
                  />
                );
              })}
              headerText={t`Табло`}
            />
            {isAdmin && (
              <EmptyButton onClick={openCreateModal} color={theme.colors.Gray96} icon={<Plus size={16} />}>
                <Trans>Добавить табло</Trans>
              </EmptyButton>
            )}
          </LeftContainer>
          <RightContainer>
            <Header templateId={template.id} />
            <CanvasWrapper>{canvas}</CanvasWrapper>
          </RightContainer>
        </Container>
      </MainBlock>
      {createModal}
    </>
  );
}

export default CatalogCard;
